import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { DeleteWorkloadsModal } from "../components/DeleteWorkloadsModal";
import { ConfirmActionModal } from "../components/ConfirmActionModal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Mono,
  PageHeader,
  StateBadge,
  SuspendedBadge,
  Table,
  Td,
  Th,
  buttonStyles,
} from "../components/ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { hourlyMicros, rate } from "../lib/money";
import { describePlan } from "../lib/plans";
import { useApp, useAppAction, useDeleteApp } from "../lib/queries";
import type { AdminAppDetail, WorkloadResult } from "../lib/types";

export function AppDetailPage() {
  const { appId = "" } = useParams();
  const { data, isPending, error } = useApp(appId);
  const canWrite = useCanWrite();
  const act = useAppAction(appId);
  const [confirmingStop, setConfirmingStop] = useState(false);
  const navigate = useNavigate();
  const remove = useDeleteApp(appId, data?.organization.id);
  const [deleting, setDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<WorkloadResult[] | null>(null);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  return (
    <>
      <PageHeader
        title={data.name}
        description={`${data.organization.name} · ${data.server?.name ?? "no server"}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to={`/audit?orgId=${data.organization.id}`} className={buttonStyles("secondary", "md")}>
              Audit activity
            </Link>
            {canWrite && (
              <>
                <Button
                  variant="secondary"
                  loading={act.isPending && act.variables === "redeploy"}
                  onClick={() => act.mutate("redeploy")}
                >
                  Redeploy
                </Button>
                {/* The engine's view, so Start is offered for an app that is
                    actually down rather than one we merely recorded as up. */}
                {data.status.value === "stopped" ? (
                  <Button
                    loading={act.isPending && act.variables === "start"}
                    onClick={() => act.mutate("start")}
                  >
                    Start
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    loading={act.isPending && act.variables === "stop"}
                    onClick={() => setConfirmingStop(true)}
                  >
                    Stop
                  </Button>
                )}
                <Button variant="danger" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </>
            )}
          </div>
        }
      />

      {act.error && !confirmingStop && <Alert>{act.error.message}</Alert>}
      {act.data?.rebuilding && (
        <Alert tone="info">
          Its image had been cleaned up from the server while it was stopped, so the start became a
          rebuild of the commit it last built. It is back when the build finishes.
        </Alert>
      )}
      {canWrite && (
        <Alert tone="info">
          These act on a customer's workload, and appear in their audit log.
        </Alert>
      )}
      {data.organization.suspendedAt && (
        <Alert tone="warning">
          <Link to={`/organizations/${data.organization.id}`} className="underline">
            {data.organization.name}
          </Link>{" "}
          is suspended.
        </Alert>
      )}
      {/*
        The abuse screen's note, for the operator who followed the alert here.
        What matched, and the two things to decide: is this a shell on a shared
        box, and if so, delete it and suspend the tenant — both a click away in
        the header and on the organization's page.
      */}
      {data.abuseFlags.length > 0 && (
        <Alert tone="warning">
          <p className="font-medium">
            Flagged by the abuse screen: {data.abuseFlags.join(", ")}.
          </p>
          <p className="mt-0.5">
            The name or source read like a web terminal, a remote desktop, a miner or a proxy. It
            was allowed because a name is a signal, not a verdict — look at what it runs. If it is
            one of those, delete it and suspend{" "}
            <Link to={`/organizations/${data.organization.id}`} className="underline">
              {data.organization.name}
            </Link>
            .
          </p>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Configuration" />
          <dl className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">
                State
              </dt>
              <dd className="mt-1 flex items-center gap-2 text-sm">
                <StateBadge state={data.status.value} stale={data.status.stale} />
                {data.runtimeStatus && (
                  <span className="text-xs text-fg-subtle">
                    The engine reports “{data.runtimeStatus}”
                  </span>
                )}
              </dd>
            </div>
            {/* What runs is the branch before a switch until somebody deploys,
                which is the first thing to know about "my change is not live". */}
            <Detail
              label="Source"
              value={`${describeSource(data.source)}${data.branchDeployPending ? " — switched, not deployed yet" : ""}`}
            />
            <Detail label="Build type" value={data.buildType} />
            <Detail label="Port" value={data.port?.toString() ?? "—"} />
            {/*
              The tier sets this app's container limits and its owner's hourly
              charge, so it belongs next to the rest of the configuration rather
              than only on the tenant's own billing page.
            */}
            <Detail
              label="Plan"
              value={
                data.plan
                  ? `${describePlan(data.plan)} · ${rate(hourlyMicros(data.plan.price.cents))}/h`
                  : "—"
              }
            />
            {/*
              Replicas multiply the plan: three of a tier is three times its
              rate, which is why the count sits beside the price rather than
              under runtime. A rule is named as one so an operator reading a
              count that keeps changing knows nobody is pressing anything.
            */}
            <Detail
              label="Replicas"
              value={
                data.autoscale
                  ? `${data.replicas} · rule ${data.autoscale.min}–${data.autoscale.max} at ${data.autoscale.targetCpu}% CPU`
                  : `${data.replicas}`
              }
            />
            <div>
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">
                Organization
              </dt>
              <dd className="mt-1 text-sm">
                <Link
                  to={`/organizations/${data.organization.id}`}
                  className="text-accent hover:underline"
                >
                  {data.organization.name}
                </Link>
                {data.organization.suspendedAt && (
                  <span className="ml-2">
                    <SuspendedBadge />
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">
                Server
              </dt>
              <dd className="mt-1 text-sm">
                {data.server ? (
                  <Link
                    to={`/servers/${data.server.id}`}
                    className="text-accent hover:underline"
                  >
                    {data.server.name}
                  </Link>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <Detail label="Created" value={formatDateTime(data.createdAt)} />
            <div>
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">
                Deployed app
              </dt>
              <dd className="mt-1 text-sm">
                {data.dokploy.applicationId ? (
                  <Mono>{data.dokploy.applicationId}</Mono>
                ) : (
                  <span className="text-warn">Not provisioned</span>
                )}
              </dd>
            </div>
          </dl>
        </Card>

        <Card>
          <CardHeader title="Addresses" />
          {/*
            The platform's own address first, then whatever the customer pointed
            at it.

            This card listed custom domains alone and said "No custom domains"
            otherwise, which reads as "this app cannot be reached" — and for most
            apps that is wrong. Every app gets a default domain on its first
            deploy whether or not anybody attaches a name to it, the API has
            always sent it, and an operator asked to look at a tenant's app is
            usually trying to open it. Leaving it out meant the one address that
            almost always exists was the one address this page would not show.
          */}
          {!data.defaultDomain && data.domains.length === 0 ? (
            <EmptyState
              title="No address"
              /* Not "no domains". An app reaches this state by never having
                 deployed, or by the best-effort provisioning having failed —
                 both worth distinguishing from a tenant who simply has not
                 attached a name of their own. */
              description="This app has never been given one, which means it has not deployed or its default domain failed to provision."
            />
          ) : (
            <ul className="divide-y divide-line px-5">
              {data.defaultDomain && (
                <li className="flex items-center justify-between gap-3 py-3 text-sm">
                  <a
                    href={`https://${data.defaultDomain}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="break-all text-accent hover:underline"
                  >
                    {data.defaultDomain}
                  </a>
                  <span className="shrink-0 text-xs text-fg-subtle">Platform</span>
                </li>
              )}
              {data.domains.map((domain) => (
                <li
                  key={domain.host}
                  className="flex items-center justify-between gap-3 py-3 text-sm"
                >
                  <a
                    href={domain.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="break-all text-accent hover:underline"
                  >
                    {domain.host}
                  </a>
                  <span className="shrink-0 text-xs text-fg-subtle">Custom</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Recent deployments"
          description="Read live from the server that hosts this app."
        />
        {data.deployments.length === 0 ? (
          <EmptyState
            title="No deployments"
            description="Never deployed, or its server is unreachable."
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>Deployment</Th>
                <Th>Status</Th>
                <Th priority="sm">Started</Th>
                <Th priority="md">Finished</Th>
              </tr>
            }
          >
            {data.deployments.map((deployment) => (
              <tr key={deployment.id}>
                <Td>
                  {deployment.title}
                  {deployment.errorMessage && (
                    <p className="mt-0.5 max-w-md truncate text-xs text-danger">
                      {deployment.errorMessage}
                    </p>
                  )}
                </Td>
                <Td className="capitalize">{deployment.status}</Td>
                <Td className="text-fg-subtle" priority="sm">
                  {formatRelative(deployment.startedAt ?? deployment.createdAt)}
                </Td>
                <Td className="text-fg-subtle" priority="md">
                  {formatDateTime(deployment.finishedAt)}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <ConfirmActionModal
        open={confirmingStop}
        onClose={() => {
          setConfirmingStop(false);
          act.reset();
        }}
        onConfirm={() =>
          act.mutate("stop", {
            onSuccess: () => {
              act.reset();
              setConfirmingStop(false);
            },
          })
        }
        title={`Stop ${data.name}?`}
        description="This immediately stops a customer's workload."
        consequence={`${data.organization.name} will stop serving traffic until someone starts this app again. The operation is recorded in the tenant audit log.`}
        confirmLabel="Stop app"
        danger
        loading={act.isPending && act.variables === "stop"}
        error={act.variables === "stop" ? act.error : null}
      />

      <DeleteWorkloadsModal
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete ${data.name}`}
        description={`${data.organization.name}'s app. The organization and everything else in it stay.`}
        items={[
          {
            kind: "app",
            id: data.id,
            name: data.name,
            detail: data.defaultDomain ?? `${data.source.type} · no address yet`,
            flag: data.organization.suspendedAt ? "suspended tenant" : undefined,
          },
        ]}
        noun="app"
        confirmName={data.organization.name}
        confirmLabel="Delete app"
        run={async ({ force, otp }) => {
          const response = await remove.mutateAsync({ force, otp });
          setDeleteResults([
            { kind: "app", id: data.id, name: data.name, deleted: true, warnings: response?.warnings ?? [] },
          ]);
        }}
        pending={remove.isPending}
        results={deleteResults}
        onDone={() => void navigate(`/organizations/${data.organization.id}`)}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

function describeSource(source: AdminAppDetail["source"]): string {
  switch (source.type) {
    // A connected git host. Worth distinguishing from a bare `github` source
    // when someone is working out why a deploy is failing: this one has a
    // deploy key and a webhook behind it that the other does not.
    case "repo":
      return `${String(source.repoFullName ?? `${source.owner}/${source.repository}`)} @ ${String(source.branch)}${source.pushDeliveryActive ? " (push)" : ""}`;
    case "github":
      return `${String(source.owner)}/${String(source.repository)} @ ${String(source.branch)}`;
    case "git":
      return `${String(source.url)} @ ${String(source.branch)}`;
    case "docker":
      return String(source.image);
    default:
      return "—";
  }
}
